((global, factory) => {
  const helpers = factory(global);

  if (typeof module === 'object' && typeof module.exports === 'object') {
    module.exports = helpers;
  } else {
    global.hbPriceBenefits = helpers;
    if (helpers && typeof helpers.autoStart === 'function') {
      helpers.autoStart(global.document);
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : window, (global) => {
  const documentRef = global?.document;

  const RATES = { 7: 0.0748, 8: 0.081, 9: 0.0872, 10: 0.0917, 11: 0.0979, 12: 0.1042 };
  const formatBRL = (() => {
    try {
      const formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });
      return (cents) => formatter.format((Number(cents) || 0) / 100);
    } catch (error) {
      return (cents) => {
        const value = (Number(cents) || 0) / 100;
        return `R$ ${value.toFixed(2).replace('.', ',')}`;
      };
    }
  })();

  function compute(priceCents, count) {
    const rate = count <= 6 ? 0 : RATES[count] || 0;
    const total = Math.round(priceCents * (1 + rate));
    const per = Math.round(total / count);
    return { count, rate, per, total };
  }

  function isPositivePrice(priceCents) {
    const normalized = Number(priceCents);
    return Number.isFinite(normalized) && normalized > 0;
  }

  function normalizeId(id) {
    return id === undefined || id === null ? '' : String(id).trim();
  }

  function getProductInfo(root) {
    return typeof root.closest === 'function' ? root.closest('product-info') : null;
  }

  function getCurrentVariantId(root) {
    const productInfo = getProductInfo(root);
    const input = productInfo?.querySelector('product-form input[name="id"], form input[name="id"]');
    return normalizeId(input?.value);
  }

  function getSectionId(root) {
    return normalizeId(root.dataset.hbSectionId || getProductInfo(root)?.dataset.section);
  }

  function getSourceBenefits(root, html) {
    if (!html || typeof html.querySelector !== 'function') return null;

    const sectionId = getSectionId(root);
    if (sectionId) {
      return html.querySelector(`[data-hb-benefits][data-hb-section-id="${sectionId}"]`);
    }

    return html.querySelector('[data-hb-benefits]');
  }

  function renderTable(tbody, priceCents) {
    if (!tbody) return;
    const doc = tbody.ownerDocument || documentRef;
    if (!doc) return;

    tbody.innerHTML = '';

    for (let count = 1; count <= 12; count += 1) {
      const { rate, per, total } = compute(priceCents, count);
      const tr = doc.createElement('tr');
      if (count === 6) {
        tr.classList.add('hb-benefits__row--highlight');
      }

      const tdCount = doc.createElement('td');
      tdCount.textContent = `${count}x`;
      tr.appendChild(tdCount);

      const tdPer = doc.createElement('td');
      tdPer.textContent = formatBRL(per);
      tr.appendChild(tdPer);

      const tdTotal = doc.createElement('td');
      tdTotal.textContent = formatBRL(total);
      tr.appendChild(tdTotal);

      const tdRate = doc.createElement('td');
      tdRate.textContent = rate === 0 ? 'sem juros' : `${(rate * 100).toFixed(2).replace('.', ',')}%`;
      tr.appendChild(tdRate);

      tbody.appendChild(tr);
    }
  }

  function renderSummary(root, priceCents) {
    if (!isPositivePrice(priceCents)) return;

    const installmentsValueEl = root.querySelector('[data-hb-installments-value]');
    const pixValueEl = root.querySelector('[data-hb-pix-value]');
    const pointsValueEl = root.querySelector('[data-hb-points-value]');

    const six = Math.round(priceCents / 6);
    const pix = Math.round(priceCents * 0.95);
    const points = Math.floor(priceCents / 100);

    if (installmentsValueEl) installmentsValueEl.textContent = `6x de ${formatBRL(six)} sem juros`;
    if (pixValueEl) pixValueEl.textContent = `${formatBRL(pix)} com 5% OFF`;
    if (pointsValueEl) pointsValueEl.textContent = `Ganhe ${points} pontos neste produto`;
  }

  function update(root, priceCents) {
    const normalized = Number(priceCents);
    if (!root || !isPositivePrice(normalized)) return;

    root.dataset.hbPrice = String(normalized);
    renderSummary(root, normalized);
    renderTable(root.querySelector('[data-hb-table-body]'), normalized);
  }

  function getSafeVariantPrice(root, payload = {}) {
    const variant = payload.variant;
    const variantId = normalizeId(variant?.id);
    if (!variantId || !isPositivePrice(variant?.price)) return null;

    const sectionId = getSectionId(root);
    const payloadSectionId = normalizeId(payload.sectionId);
    if (sectionId && payloadSectionId && sectionId !== payloadSectionId) return null;

    const currentVariantId = getCurrentVariantId(root);
    if (sectionId && !payloadSectionId && !currentVariantId) return null;
    if (currentVariantId && currentVariantId !== variantId) return null;

    const sourceBenefits = getSourceBenefits(root, payload.html);
    const sourceVariantId = normalizeId(sourceBenefits?.dataset.hbVariantId);
    const sourcePrice = Number(sourceBenefits?.dataset.hbPrice);

    if (sourceVariantId && sourceVariantId !== variantId) return null;
    if (isPositivePrice(sourcePrice)) {
      if (Number(sourcePrice) !== Number(variant.price)) return null;
      return sourcePrice;
    }

    return Number(variant.price);
  }

  function updateFromVariantPayload(root, payload) {
    const priceCents = getSafeVariantPrice(root, payload);
    if (!priceCents) return;

    root.dataset.hbVariantId = normalizeId(payload.variant.id);
    update(root, priceCents);
  }

  function attachPubSubVariantListener(root) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object' || !PUB_SUB_EVENTS.variantChange) return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      updateFromVariantPayload(root, event?.data || {});
    });
  }

  function attachVariantListeners(root, doc = documentRef) {
    attachPubSubVariantListener(root);
    if (!doc) return;

    const handler = (event) => updateFromVariantPayload(root, event?.detail || {});

    doc.addEventListener('variant:change', handler);
    doc.addEventListener('theme:variant:change', handler);
    doc.addEventListener('product:variant-change', handler);
  }

  function init(root, doc = documentRef) {
    if (!root || root.__hbBenefitsInitialized) return;
    root.__hbBenefitsInitialized = true;

    const priceCents = Number(root.dataset.hbPrice || 0) || 0;
    update(root, priceCents);
    attachVariantListeners(root, doc);
  }

  function start(doc = documentRef) {
    if (!doc) return;
    doc.querySelectorAll('[data-hb-benefits]').forEach((root) => init(root, doc));
  }

  function autoStart(doc = documentRef) {
    if (!doc) return;

    const run = () => start(doc);

    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  return {
    RATES,
    formatBRL,
    compute,
    renderTable,
    renderSummary,
    update,
    getSafeVariantPrice,
    updateFromVariantPayload,
    attachVariantListeners,
    init,
    start,
    autoStart,
  };
});
